import { router } from '@inertiajs/react'
import { Check, CreditCard, Loader2, X } from 'lucide-react'
import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { Subscription, SubscriptionEntitlement } from '@/types/shared'

interface BillingCardProps {
  subscription: Subscription | null
  billingEmail: string | null
}

export function BillingCard({ subscription, billingEmail }: BillingCardProps) {
  const { withAccount } = useRouteParams()
  const [email, setEmail] = useState(billingEmail || '')
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingPortal, setIsLoadingPortal] = useState(false)

  const handleManageBilling = () => {
    setIsLoadingPortal(true)
    router.visit(routes.billing.portal.path())
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null
    return new Date(dateString).toLocaleDateString()
  }

  const formatEntitlementValue = (ent: SubscriptionEntitlement): React.ReactNode => {
    if (ent.value_type === 'boolean') {
      return ent.value ? (
        <Check className="size-4 text-primary" />
      ) : (
        <X className="size-4 text-muted-foreground" />
      )
    }
    if (ent.value_type === 'integer') {
      const val = ent.value as number
      return val === -1 ? 'Unlimited' : val.toLocaleString()
    }
    return String(ent.value)
  }

  const handleSaveBillingEmail = () => {
    setIsSaving(true)
    router.patch(
      routes.settings.update.path(withAccount()),
      { account: { billing_email: email }, tab: 'billing' },
      {
        preserveScroll: true,
        onFinish: () => setIsSaving(false),
      }
    )
  }

  const emailChanged = email !== (billingEmail || '')

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="size-5" />
          Billing
        </CardTitle>
        <CardDescription>
          Manage your subscription and billing
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="billing-email">Billing Email</Label>
          <div className="flex gap-2">
            <Input
              id="billing-email"
              type="email"
              placeholder="billing@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={handleSaveBillingEmail}
              disabled={!emailChanged || isSaving}
              className="cursor-pointer"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Invoices and payment notifications will be sent to this email address.
          </p>
        </div>

        <div className="border-t border-border pt-6" />

        {subscription ? (
          <>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{subscription.plan_name} Plan</p>
                <p className="text-sm text-muted-foreground capitalize">
                  Status: {subscription.status}
                </p>
                {subscription.current_period_end && (
                  <p className="text-sm text-muted-foreground">
                    Renews: {formatDate(subscription.current_period_end)}
                  </p>
                )}
              </div>
            </div>

            {subscription.entitlements && subscription.entitlements.length > 0 && (
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-sm font-medium mb-3">Your plan includes:</p>
                <ul className="space-y-2">
                  {subscription.entitlements.map(ent => (
                    <li key={ent.key} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{ent.name}</span>
                      <span className="font-medium">{formatEntitlementValue(ent)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => router.visit(routes.billing.plans.path())}
                className="cursor-pointer"
              >
                Change Plan
              </Button>
              {subscription.plan_slug !== 'free' && (
                <Button
                  variant="outline"
                  onClick={handleManageBilling}
                  disabled={isLoadingPortal}
                  className="cursor-pointer"
                >
                  {isLoadingPortal ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Manage Billing'
                  )}
                </Button>
              )}
            </div>
          </>
        ) : (
          <p className="text-muted-foreground">No subscription found</p>
        )}
      </CardContent>
    </Card>
  )
}
